'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { useContext, useEffect, useState } from 'react'
import {
  FaArrowUp,
  FaChevronRight,
  FaEyeSlash,
  FaFemale,
  FaFire,
  FaLongArrowAltRight,
  FaMagic,
  FaMobile,
  FaUsers,
  FaVideo,
} from 'react-icons/fa'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'

import AdCard from '../../component/AdCard'
import Card from '../../component/Card'
import { useAuth } from '../../contexts/AuthContext'
import { ImageContext } from '../../contexts/ImageContext'

import Link from 'next/link'
import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { ClipLoader } from 'react-spinners'

import { staticImagesSuperSafe } from '../../config/staticImages'
import { restrictedWords } from '../../helpers/config'
import {
  activeButtonStyle,
  containsRestrictedWords,
  growthFunction,
  inactiveButtonStyle,
  isSafeCountry,
} from '../../helpers/helpers'

import { useTranslation } from 'react-i18next'
import {
  BsBrushFill,
  BsDashCircle,
  BsIncognito,
  BsLightningChargeFill,
} from 'react-icons/bs'

const AiGeneratorLandingBottomHalf = ({
  upgradeModal,
  setUpgradeModal,
  // The parent (AiGeneratorLanding) owns the safe-mode warning modal and already
  // passes this setter — it was referenced in the Video-tab onClick but never
  // accepted here, so safe-mode users got a ReferenceError instead of the warning.
  setSafeWarningOpen,
}) => {
  const { t } = useTranslation()

  const searchParams = useSearchParams()
  const searchQuery = searchParams.get('searchQuery')

  const { user, subscription, safe, authResolved } = useAuth()

  const router = useRouter()
  const {
    activeButton,
    setActiveButton,
    topImages,
    setTopImages,
    featuredImages,
    setFeaturedImages,
    videoImages,
    setVideoImages,
    model,
    setModel,
    totalImageCount,
    setTotalImageCount,
    images,
    setImages,
    filteredImages,
    setFilteredImages,
  } = useContext(ImageContext)

  const [fetching, setFetching] = useState(false)
  const [searchModel, setSearchModel] = useState('All')
  const [loadingImages, setLoadingImages] = useState(false)

  useEffect(() => {
    if (totalImageCount > 0) {
      return
    }
    if (!user) {
      return
    }

    const fetchTotalImages = async () => {
      if (!user) {
        return
      }
      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/total_images`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${await user.getIdToken()}`,
              'Content-Type': 'application/json',
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to fetch total images')
        }

        const data = await response.json()
        setTotalImageCount(data.total_images)
      } catch (error) {
        console.error('Error fetching total images:', error)
      }
    }

    fetchTotalImages()
  }, [totalImageCount, user])

  useEffect(() => {
    const fetchSearch = async () => {
      if (!user) {
        // Public route: this component mounts before Firebase resolves on EVERY load,
        // so redirecting on a transient null user ejected signed-in visitors who
        // arrived with ?searchQuery. Redirect only once auth has actually resolved,
        // and bail either way — the code below dereferences `user`.
        if (authResolved) router.push('/signup')
        return
      }

      setImages([])

      const idToken = await user.getIdToken()
      const userId = user && user.uid ? user.uid : null

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/search`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            query: searchQuery,
            user: userId,
            model: searchModel,
          }),
        },
      )

      const searchResults = await response.json()

      const imageList = []
      searchResults.forEach((data) => {
        imageList.push({
          id: data.id,
          title: data.prompt,
          imageUrl: data.image_url.replace(
            process.env.NEXT_PUBLIC_URL_1,
            process.env.NEXT_PUBLIC_URL_2,
          ),
          description: data.prompt,
          likes: growthFunction(data.likes) || 0,
          nsfw: data.nsfw,
          date: data.date_uploaded,
          uid: data.uid,
          model: data.model,
          width: data.width,
          height: data.height,
          creativity: data.cfg_scale,
          negativePrompt: data.negative_prompt,
          loras: data.loras,
          views: growthFunction(data.views) || 0,
          upscaled: data.upscaled,
          seed: data.seed,
          reports: data.reports || 0,
          filter: data.style_filter,
          prompt_ja: data?.prompt_ja,
          prompt_de: data?.prompt_de,
          prompt_zh: data?.prompt_zh,
          prompt_es: data?.prompt_es,
          negative_prompt_ja: data?.negative_prompt_ja,
          negative_prompt_de: data?.negative_prompt_de,
          negative_prompt_zh: data?.negative_prompt_zh,
          negative_prompt_es: data?.negative_prompt_es,
          details: data?.details,
          expression: data?.expression,
          video: data?.video || false,
          quality: data?.quality || 'Ultra',
          videoThumbnail: data?.videoThumbnail,
        })
      })

      setImages(imageList)
    }

    if (searchQuery && searchQuery.trim() !== '') {
      fetchSearch()
      setActiveButton('Search')
    }
  }, [searchQuery, setActiveButton, user, authResolved, setImages, searchModel])

  useEffect(() => {
    const filteredImages = images.filter((img) => {
      const isRestricted = containsRestrictedWords(img.title, restrictedWords)
      const isTopOrLowReports =
        activeButton === 'Top' ||
        img.reports < 3 ||
        img.uid === '57qlmsb4JlTEpZmuooqlTgj1rLy2'
      const isNotDisturbing = ![
        'POV Strangling',
        'Penis',
        'Tied Up',
        'Tape',
        'Ballgag',
        'Suspension',
        'POV Choking',
      ].includes(img.loras)
      const isNotFur =
        img.model !== 'Fur' ||
        model === 'Fur' ||
        img.model !== 'Furtoon' ||
        model === 'Furtoon'
      const isNotReal = true

      return (
        !isRestricted &&
        isTopOrLowReports &&
        isNotFur &&
        isNotReal &&
        isNotDisturbing
      )
    })

    if (searchQuery || !user) {
      setFilteredImages(images)
    } else {
      setFilteredImages(filteredImages)
    }
  }, [images])

  useEffect(() => {
    let imagesToSet = []

    setImages(imagesToSet)
    if (
      topImages[model].length > 0 ||
      featuredImages[model].length > 0 ||
      videoImages[model].length > 0
    ) {
      imagesToSet =
        activeButton === 'Top'
          ? topImages[model]
          : activeButton === 'Video'
            ? videoImages[model]
            : featuredImages[model]
      setImages(imagesToSet)
    } else if (!user) {
      setImages(staticImagesSuperSafe)
    }
    loadMoreImages()
  }, [model, activeButton])

  const fetchFromApi = async () => {
    try {
      const idToken = await user.getIdToken()

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/search_by_uids`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken, // Assuming you have the token stored in user object
          },
        },
      )

      if (!response.ok) {
        throw new Error('API call failed')
      }

      const data = await response.json()

      // Transform fetched data to the same structure as the other fetch methods
      const imageList = []
      data.forEach((data) => {
        imageList.push({
          id: data.id,
          title: data.prompt,
          imageUrl: data.image_url.replace(
            process.env.NEXT_PUBLIC_URL_1,
            process.env.NEXT_PUBLIC_URL_2,
          ),
          description: data.prompt,
          likes: growthFunction(data.likes) || 0,
          nsfw: data.nsfw,
          date: data.date_uploaded,
          uid: data.uid,
          model: data.model,
          width: data.width,
          height: data.height,
          creativity: data.cfg_scale,
          negativePrompt: data.negative_prompt,
          loras: data.loras,
          views: growthFunction(data.views) || 0,
          upscaled: data.upscaled,
          seed: data.seed,
          reports: data.reports || 0,
          filter: data.style_filter,
          prompt_ja: data?.prompt_ja,
          prompt_de: data?.prompt_de,
          prompt_zh: data?.prompt_zh,
          prompt_es: data?.prompt_es,
          negative_prompt_ja: data?.negative_prompt_ja,
          negative_prompt_de: data?.negative_prompt_de,
          negative_prompt_zh: data?.negative_prompt_zh,
          negative_prompt_es: data?.negative_prompt_es,
          details: data?.details,
          expression: data?.expression,
          video: data?.video || false,
          quality: data?.quality || 'Ultra',
          videoThumbnail: data?.videoThumbnail,
        })
      })

      return imageList
    } catch (error) {
      console.error('Error fetching from API: ', error)
      return []
    } finally {
      setLoadingImages(false)
    }
  }

  const loadMoreImages = async () => {
    if (!fetching) {
      const fetchImages = async () => {
        try {
          if (loadingImages || searchQuery) {
            return
          }

          setLoadingImages(true)
          setFetching(true)

          if (activeButton === 'Following') {
            const images = await fetchFromApi()
            setImages(images)
            return
          }

          const idToken = await user.getIdToken()

          const endpoint =
            activeButton === 'Top'
              ? `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/get_top_feed`
              : activeButton === 'Video'
                ? `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/get_video_feed`
                : `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/get_newest_feed`

          const lastDocId =
            topImages[model]?.length > 0
              ? topImages[model][topImages[model].length - 1].id
              : null

          const lastDocIdVideo =
            videoImages[model]?.length > 0
              ? videoImages[model][videoImages[model].length - 1].id
              : null

          const lastDocIdNewest =
            featuredImages[model]?.length > 0
              ? featuredImages[model][featuredImages[model].length - 1].id
              : null

          const response = await fetch(endpoint, {
            method: 'POST',
            body: JSON.stringify({
              last_doc_id_newest: lastDocIdNewest,
              last_doc_id_video: lastDocIdVideo,
              last_doc_id: lastDocId,
              safe,
              model,
            }),
            headers: {
              'Content-Type': 'application/json',
              Authorization: idToken,
            },
          })

          if (response.ok) {
            const responseData = await response.json()
            const newImages = responseData.images

            if (newImages.length > 0) {
              let imageList = []
              newImages.forEach((data) => {
                imageList.push({
                  id: data.id,
                  title: data.prompt,
                  imageUrl: data.image_url.replace(
                    process.env.NEXT_PUBLIC_URL_1,
                    process.env.NEXT_PUBLIC_URL_2,
                  ),
                  description: data.prompt,
                  likes: growthFunction(data.likes) || 0,
                  nsfw: data.nsfw,
                  date: data.date_uploaded,
                  uid: data.uid,
                  model: data.model,
                  width: data.width,
                  height: data.height,
                  creativity: data.cfg_scale,
                  negativePrompt: data.negative_prompt,
                  loras: data.loras,
                  views: growthFunction(data.views) || 0,
                  upscaled: data.upscaled,
                  seed: data.seed,
                  reports: data.reports || 0,
                  filter: data.style_filter,
                  prompt_ja: data?.prompt_ja,
                  prompt_de: data?.prompt_de,
                  prompt_zh: data?.prompt_zh,
                  prompt_es: data?.prompt_es,
                  negative_prompt_ja: data?.negative_prompt_ja,
                  negative_prompt_de: data?.negative_prompt_de,
                  negative_prompt_zh: data?.negative_prompt_zh,
                  negative_prompt_es: data?.negative_prompt_es,
                  details: data?.details,
                  expression: data?.expression,
                  video: data?.video || false,
                  quality: data?.quality || 'Ultra',
                  videoThumbnail: data?.videoThumbnail,
                })
              })

              if (safe) {
                imageList = imageList.filter((image) => !image.video)
              }

              if (activeButton === 'Top') {
                setTopImages((prevState) => ({
                  ...prevState,
                  [model]: [...prevState[model], ...imageList],
                }))
              } else if (activeButton === 'Video') {
                setVideoImages((prevState) => ({
                  ...prevState,
                  [model]: [...prevState[model], ...imageList],
                }))
              } else {
                setFeaturedImages((prevState) => ({
                  ...prevState,
                  [model]: [...prevState[model], ...imageList],
                }))
              }
              setImages((prevImages = []) => [...prevImages, ...imageList])
            }
          }
        } catch {
          // console.log(error)
        } finally {
          setLoadingImages(false)
          setFetching(false)
        }
      }

      return await fetchImages()
    }
  }

  useBottomScrollListener(loadMoreImages, {
    offset: 2000, // Offset from the bottom of the page. Adjust this value as per your requirement.
    debounce: 1000,
  })

  const isPaidUser =
    subscription?.plan === 'Studio' ||
    subscription?.plan === 'Plus' ||
    subscription?.plan === 'Premium' ||
    subscription?.plan === 'Pro'

  return (
    <div className="py-3 px-1 lg:px-8 mx-auto">
      <section className="container mx-auto md:px-4 py-8">
        <div className="flex items-center justify-between mb-4 ">
          {searchQuery ? (
            <div className="flex flex-col">
              <div className="relative inline-flex md:pl-2 mb-3">
                <svg
                  className="w-2 h-2 absolute top-0 right-0 m-4 pointer-events-none"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 412 232"
                >
                  <path
                    d="M206 232l206-232H0z"
                    fill="#fff"
                    fillRule="evenodd"
                  ></path>
                </svg>
                <select
                  id="modelSelector"
                  value={searchModel}
                  onChange={(e) => {
                    setSearchModel(e.target.value)
                  }}
                  className={`rounded-full font-semibold w-40 text-center h-10 px-8 hover:border-gray-400 focus:outline-none appearance-none ${
                    searchModel === 'All'
                      ? inactiveButtonStyle
                      : activeButtonStyle
                  }`}
                >
                  {/* "All" (not "all") — it has to match this select's initial
                      state, which is what the search API reads as "no style
                      filter". The lowercase value made an explicit "All Styles"
                      pick filter on a style no image has, returning nothing. */}
                  <option value="All">{t('allStyles')}</option>
                  <option value="Real">{t('real')}</option>
                  <option value="Hyperreal"> {t('hyperreal')}</option>
                  <option value="Anime">{t('anime')}</option>
                  <option value="Anime XL">{t('anime xl')}</option>
                  <option value="Cinematic XL">{t('cinematic xl')}</option>
                  <option value="Hardcore XL">{t('hardcore xl')}</option>
                  <option value="Hyperanime">{t('hyperanime')}</option>
                  <option value="K-Pop">{t('k-pop')}</option>
                </select>
              </div>
              <p className="text-white font-semibold">{t('searchResults')}</p>
            </div>
          ) : (
            <div className="flex flex-col">
              <>
                <div className="inline-flex items-center md:space-x-2 space-x-1 flex-wrap">
                  <button
                    className={`${
                      activeButton === 'Top'
                        ? activeButtonStyle
                        : inactiveButtonStyle
                    } font-semibold md:w-32 w-11 text-center md:py-2 py-3 rounded-full flex items-center justify-center mb-1`}
                    aria-label="Top images"
                    onClick={() => {
                      if (fetching) {
                        return
                      }
                      setActiveButton('Top')
                    }}
                  >
                    <FaArrowUp className="md:mr-1" />
                    <p className="md:block hidden text-sm">{t('top')}</p>
                  </button>

                  <button
                    className={`${
                      activeButton === 'Feed'
                        ? activeButtonStyle
                        : inactiveButtonStyle
                    } font-semibold md:w-32 w-11 text-center md:py-2 py-3 rounded-full flex items-center justify-center mb-1`}
                    aria-label="Newest images"
                    onClick={() => {
                      if (fetching) {
                        return
                      }
                      setActiveButton('Feed')
                    }}
                  >
                    <FaFire className="md:mr-1" />
                    <p className="md:block hidden text-sm">{t('newest')}</p>
                  </button>

                  {/* Video is Safe-Mode-gated, and safe-country visitors can
                      never turn Safe Mode off — hide the tab instead of pointing
                      them at a Settings toggle that isn't rendered for them. */}
                  {!isSafeCountry() && (
                    <button
                      className={`${
                        activeButton === 'Video'
                          ? activeButtonStyle
                          : inactiveButtonStyle
                      } font-semibold md:w-32 w-11 text-center md:py-2 py-3 rounded-full flex items-center justify-center mb-1`}
                      aria-label="Videos"
                      onClick={() => {
                        if (safe) {
                          setSafeWarningOpen(true)
                          return
                        }
                        if (fetching) {
                          return
                        }
                        setActiveButton('Video')
                      }}
                    >
                      <FaVideo className="md:mr-1" />
                      <p className="md:block hidden text-sm">{t('video')}</p>
                    </button>
                  )}

                  <button
                    className={`${
                      activeButton === 'Following'
                        ? activeButtonStyle
                        : inactiveButtonStyle
                    }  font-semibold md:w-32 w-11 text-center md:py-2 py-3 rounded-full flex items-center justify-center mb-1`}
                    aria-label="Following"
                    onClick={() => {
                      if (fetching) {
                        return
                      }
                      user
                        ? setActiveButton('Following')
                        : router.push('/signup')
                    }}
                  >
                    <FaUsers className="md:mr-1" />
                    <p className="md:block hidden text-sm">{t('following')}</p>
                  </button>

                  <div className="hidden sm:block border-r border-gray-500 h-8 self-center pl-2"></div>

                  <div className="relative inline-flex md:pl-2">
                    <svg
                      className="w-2 h-2 absolute top-0 right-0 m-4 pointer-events-none"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 412 232"
                    >
                      <path
                        d="M206 232l206-232H0z"
                        fill="#fff"
                        fillRule="evenodd"
                      ></path>
                    </svg>
                    <select
                      onChange={(e) => {
                        user ? setModel(e.target.value) : router.push('/signup')
                      }}
                      name="Style selector"
                      aria-label="Style"
                      className={`rounded-full font-semibold w-40 text-center h-9 px-8 hover:border-gray-400 focus:outline-none appearance-none text-sm cursor-pointer ${
                        model === 'all'
                          ? inactiveButtonStyle
                          : activeButtonStyle
                      }`}
                    >
                      <option value="all">{t('allStyles')}</option>
                      <option value="Real">{t('real')}</option>
                      <option value="Hyperreal"> {t('hyperreal')}</option>
                      <option value="Anime">{t('anime')}</option>
                      <option value="Anime XL">{t('anime xl')}</option>
                      <option value="Cinematic XL">{t('cinematic xl')}</option>
                      <option value="Hardcore XL">{t('hardcore xl')}</option>
                      <option value="Hyperanime">{t('hyperanime')}</option>
                      <option value="K-Pop">{t('k-pop')}</option>
                    </select>
                  </div>
                </div>

                {user && (
                  <p className="text-white font-semibold mt-4">
                    {t('results')} {totalImageCount.toLocaleString('en-US')}
                  </p>
                )}
              </>
            </div>
          )}
        </div>
        {safe && !isSafeCountry() && (
          <p className="text-white bg-[var(--foreground-bg-color)] px-4 py-2 rounded-md flex items-center mb-2 md:w-1/2 flex items-center justify-center ml-auto">
            <FaEyeSlash className="mr-2 text-[var(--main-bg-color)] text-2xl" />
            {t('safeModeEnabled')}
            <Link
              className="underline ml-2 hover:text-[var(--main-bg-color)]"
              href="/settings"
            >
              Update Settings
            </Link>
          </p>
        )}
        <>
          {filteredImages && filteredImages.length > 0 ? (
            <>
              {!isPaidUser ? (
                <ResponsiveMasonry
                  columnsCountBreakPoints={{
                    350: 1,
                    750: 2,
                    1000: 3,
                    1750: 4,
                  }}
                >
                  <Masonry gutter="0.5rem">
                    {Array.from({
                      length: filteredImages.length,
                    }).map((_, index) => {
                      const image = filteredImages[index]
                      if (!image) return null

                      // Array to store the Card and potentially an AdCard
                      const elements = []

                      // Push the main card
                      elements.push(
                        <div
                          key={image.id}
                          className="ring-1 ring-[var(--secondary-bg-color)] overflow-hidden rounded-md"
                        >
                          <Card
                            {...image}
                            popImage={(imageId) => {
                              setImages(
                                images.filter((img) => img.id !== imageId),
                              )
                            }}
                            imageData={image}
                            upgradeModal={upgradeModal}
                            setUpgradeModal={setUpgradeModal}
                            nsfw={false}
                            shouldLazyLoad={true}
                          />
                        </div>,
                      )

                      // Check if the index is a multiple of 12 and not 0, then add AdCard
                      if ((index + 1) % 20 === 0 && index !== 0) {
                        elements.push(
                          <div className="ring-1 ring-[var(--secondary-bg-color)] overflow-hidden rounded-md">
                            <AdCard key={`ad-${index}`} />
                          </div>,
                        )
                      }

                      return elements
                    })}
                  </Masonry>
                </ResponsiveMasonry>
              ) : (
                <ResponsiveMasonry
                  columnsCountBreakPoints={{
                    350: 1,
                    750: 2,
                    1000: 3,
                    1750: 4,
                  }}
                >
                  <Masonry gutter="0.5rem">
                    {Array.from({
                      length: filteredImages.length,
                    }).map((_, index) => {
                      const image = filteredImages[index]
                      if (!image) return null

                      return (
                        <div
                          key={image.id}
                          className="ring-1 ring-[var(--secondary-bg-color)] overflow-hidden rounded-md"
                        >
                          <Card
                            {...image}
                            popImage={(imageId) => {
                              setImages(
                                images.filter((img) => img.id !== imageId),
                              )
                            }}
                            imageData={image}
                            upgradeModal={upgradeModal}
                            setUpgradeModal={setUpgradeModal}
                            nsfw={false}
                          />
                        </div>
                      )
                    })}
                  </Masonry>
                </ResponsiveMasonry>
              )}
            </>
          ) : loadingImages ? (
            <p className="text-white text-center my-12">
              <ClipLoader color="#ffffff" size={24} />
            </p>
          ) : (
            <>
              {activeButton === 'Following' && filteredImages.length === 0 ? (
                <p className="text-white text-center my-12">
                  No images from the users you're following. Start following
                  users to see their content here.
                </p>
              ) : (
                <p className="text-white text-center my-12">No images found</p>
              )}
            </>
          )}

          {!user ? (
            <>
              <Link href="/signup">
                <div className="flex items-center justify-center px-5 w-72 max-w-full py-3 my-20 bg-[var(--main-bg-color)] text-white rounded-full focus:outline-none ml-auto mr-auto font-semibold shadow-lg md:text-lg text-center transition-transform duration-200 hover:scale-[1.05]">
                  {t('signUpToViewMore')}{' '}
                  <FaLongArrowAltRight className="ml-2" />
                </div>
              </Link>
            </>
          ) : null}
        </>
        {!user ? (
          <>
            <div className="flex-grow border-t border-gray-800 my-6"></div>
            <div className="my-12 mx-auto md:w-1/2">
              <div className="p-2">
                {' '}
                <Link href="/signup">
                  <img
                    loading="lazy"
                    src="/assets/landing_create_safe.webp"
                    className="rounded-xl"
                    alt="Create any AI image"
                  />
                </Link>{' '}
              </div>
              <div className="p-2 my-auto">
                <h2 className="text-3xl font-semibold tracking-tight text-white mb-1 flex items-center">
                  Create Any AI Image
                </h2>
                <p className="tracking-tight text-gray-300">
                  Generate any AI image you can imagine with Promptchan AI, in a
                  range of styles from anime to cinematic and fine art.
                  Promptchan uses advanced models for AI image creation, so you
                  can design AI art that matches what you pictured.
                </p>
                <Link href="/signup">
                  <button className="text-[#786FFF] text-xl my-3 flex items-center">
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>
            </div>
            <div className="flex-grow border-t border-gray-800 my-6"></div>
            <div className="my-24 md:flex">
              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-3xl font-semibold tracking-tight text-white mb-1 flex items-center">
                  <FaMagic className="mr-2" /> {t('cloneAiArtSafe')}
                </h2>
                <p className="tracking-tight text-gray-300">
                  {t('exploreOverSafe')}
                </p>
                <Link href="/signup">
                  <button className="text-[#786FFF] text-xl my-3 flex items-center">
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>
              <div className="md:w-1/2 p-2">
                {' '}
                <Link href="/signup">
                  <img
                    loading="lazy"
                    src="/assets/cloneImageArtSafe.webp"
                    className="rounded-xl"
                    alt="Clone AI Art"
                  />
                </Link>{' '}
              </div>
            </div>
            <div className="my-24 md:flex">
              <div className="md:w-1/2 p-2">
                <Link href="/signup">
                  <img
                    loading="lazy"
                    src="/assets/landing_pose_safe2.webp"
                    className="rounded-xl"
                    alt="AI Posing"
                  />
                </Link>
              </div>
              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-3xl font-semibold tracking-tight text-white mb-1 flex items-center justify-end">
                  <FaFemale className="mr-2" />
                  {t('AIPosing')}
                </h2>
                <p className="tracking-tight text-gray-300 text-right">
                  {t('promptchanAiPoseFeatureSafe')}
                </p>
                <div className="justify-end flex">
                  <Link href="/signup">
                    <button className="text-[#786FFF] text-xl my-3 flex items-center">
                      {t('getStarted')} <FaChevronRight className="ml-2" />
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            <div className="my-24 md:flex">
              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-3xl font-semibold tracking-tight text-white mb-1 flex items-center">
                  <BsBrushFill className="mr-2" /> {t('anyAiArtStyle')}
                </h2>
                <p className="tracking-tight text-gray-300">
                  {t('chooseFromAWideSafe')}
                </p>
                <Link href="/signup">
                  <button className="text-[#786FFF] text-xl my-3 flex items-center">
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>
            </div>
            <div className="flex-grow border-t border-gray-800 my-6"></div>
            <div className="my-24 md:flex">
              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-3xl font-semibold tracking-tight text-white mb-1 flex items-center">
                  <BsIncognito className="mr-2" /> {t('privateAiGenerating')}
                </h2>
                <p className="tracking-tight text-gray-300">
                  Generate in privacy without worrying about your data being
                  shared or used for AI training.
                </p>
                <Link href="/signup">
                  <button className="text-[#786FFF] my-3 flex items-center">
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>

              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-3xl font-semibold tracking-tight text-white mb-1 flex items-center">
                  <BsDashCircle className="mr-2" /> More with Less
                </h2>
                <p className="tracking-tight text-gray-300">
                  Promptchan aims to be the most simple and intuitive platform
                  for AI image generation.
                </p>
                <Link href="/signup">
                  <button className="text-[#786FFF] my-3 flex items-center">
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>
            </div>
            <div className="my-24 md:flex">
              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-3xl font-semibold tracking-tight text-white mb-1 flex items-center">
                  <FaMobile className="mr-2" /> {t('mobileGirlGeneration')}
                </h2>
                <p className="tracking-tight text-gray-300">{t('createAi')}</p>
                <Link href="/signup">
                  <button className="text-[#786FFF] my-3 flex items-center">
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>

              <div className="md:w-1/2 p-2 my-auto">
                <h2 className="text-3xl font-semibold tracking-tight text-white mb-1 flex items-center">
                  <BsLightningChargeFill className="mr-2" />{' '}
                  {t('fastestGirlGenerator')}
                </h2>
                <p className="tracking-tight text-gray-300">
                  {t('stopWaitingSafe')}
                </p>
                <Link href="/signup">
                  <button className="text-[#786FFF] my-3 flex items-center">
                    {t('getStarted')} <FaChevronRight className="ml-2" />
                  </button>
                </Link>
              </div>
            </div>

            <div
              style={{
                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.2) 10%, rgba(11, 34, 99, 0.8)), url(/assets/header.webp)`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
              }}
              className="h-60 flex flex-col items-center justify-center rounded-xl mt-20"
            >
              <h2 className="text-white md:text-2xl font-semibold text-center">
                Try out the Most Advanced AI Image Generator
              </h2>
              <Link href="/signup">
                <div className="flex items-center justify-center px-5 w-72 max-w-full py-3 my-4 bg-white text-[var(--main-bg-color)] rounded-full focus:outline-none ml-auto mr-auto font-semibold shadow-lg md:text-lg text-center transition-transform duration-200 hover:scale-[1.05]">
                  {t('tryItNow')}
                  <FaLongArrowAltRight className="ml-2" />
                </div>
              </Link>
            </div>
          </>
        ) : null}

        {!user && (
          <div className="flex items-center justify-center py-12">
            <a
              href="https://theresanaiforthat.com/ai/promptchan/?ref=featured&v=1673829"
              target="_blank"
              rel="nofollow"
            >
              <img
                width="300"
                src="https://media.theresanaiforthat.com/featured-on-taaft.png?width=600"
                alt="Featured on There's an AI for that"
              />
            </a>
          </div>
        )}
      </section>
    </div>
  )
}

export default AiGeneratorLandingBottomHalf
